import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

// Компоненты
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import RealTimeActivity from './pages/RealTimeActivity';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Stakings from './pages/Stakings';
import BurnPool from './pages/BurnPool';
import MiniGames from './pages/MiniGames';
import Withdrawals from './pages/Withdrawals';
import PushAlerts from './pages/PushAlerts';
import TasksManagement from './pages/TasksManagement';
import VipRanksManagement from './pages/VipRanksManagement';
import TonIntegration from './pages/TonIntegration';
import SlotsSettings from './pages/SlotsSettings';
import NotificationSettings from './pages/NotificationSettings';
import TokenAnalytics from './pages/TokenAnalytics';
import MarketMaker from './pages/MarketMaker';
import WalletScanner from './pages/WalletScanner';
import Jarvis from './pages/Jarvis';
import Promo from './pages/Promo';
import Loyalty from './pages/Loyalty';
import Partners from './pages/Partners';
import AdminLayout from './components/AdminLayout';

// Контекст авторизации
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Создаем тему Material-UI
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6366f1',
    },
    secondary: {
      main: '#8b5cf6',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    // Заголовки на телефоне ужимаем: крупный шрифт распирал карточки
    // и вылезал за край экрана (жалоба владельца 28.07.2026)
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      '@media (max-width:600px)': { fontSize: '1.6rem' },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      '@media (max-width:600px)': { fontSize: '1.45rem' },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      '@media (max-width:600px)': { fontSize: '1.35rem' },
    },
    h4: {
      '@media (max-width:600px)': { fontSize: '1.25rem' },
    },
    h5: {
      '@media (max-width:600px)': { fontSize: '1.1rem' },
    },
    h6: {
      '@media (max-width:600px)': { fontSize: '1rem' },
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    // Кнопки с длинными подписями («НАЗНАЧИТЬ МЕНЕДЖЕРА») на телефоне
    // не влезали в строку и уезжали за экран
    MuiButton: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          '@media (max-width:600px)': {
            fontSize: '0.72rem',
            padding: '5px 10px',
            lineHeight: 1.3,
            minWidth: 0,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { padding: 6 } },
      },
    },
    // Широкие таблицы должны скроллиться ВНУТРИ себя, а не тащить
    // вбок всю страницу
    MuiTableContainer: {
      styleOverrides: {
        root: { maxWidth: '100%', overflowX: 'auto' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 6px',
            fontSize: '0.78rem',
          },
        },
      },
    },
    // Диалоги на телефоне занимали не всю ширину и обрезали кнопки
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 12,
            width: 'calc(100% - 24px)',
            maxWidth: 'calc(100% - 24px)',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { flexWrap: 'wrap', gap: 8 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { maxWidth: '100%' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { fontSize: '0.82rem' } },
        message: { minWidth: 0, overflowWrap: 'anywhere' },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { padding: 12 } },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="activity-live" element={<RealTimeActivity />} />
              <Route path="users" element={<Users />} />
              <Route path="tasks" element={<TasksManagement />} />
              <Route path="vip-ranks" element={<VipRanksManagement />} />
              <Route path="settings" element={<Settings />} />
              <Route path="stakings" element={<Stakings />} />
              <Route path="burn-pool" element={<BurnPool />} />
              <Route path="minigames" element={<MiniGames />} />
              <Route path="withdrawals" element={<Withdrawals />} />
              <Route path="push-alerts" element={<PushAlerts />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="ton-integration" element={<TonIntegration />} />
              <Route path="slots-settings" element={<SlotsSettings />} />
              <Route path="token-analytics" element={<TokenAnalytics />} />
              <Route path="market-maker" element={<MarketMaker />} />
              <Route path="wallet-scanner" element={<WalletScanner />} />
              <Route path="jarvis" element={<Jarvis />} />
              <Route path="promo" element={<Promo />} />
              <Route path="loyalty" element={<Loyalty />} />
              <Route path="partners" element={<Partners />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;